/* eslint-disable @typescript-eslint/no-unused-vars */
import "@iventis/styles/src/fonts/index.css";
import React, { useRef, useState } from "react";
import { StyleType } from "@iventis/domain-model/model/styleType";
import center from "@turf/center";
import { getStaticAndMappedValues } from "@iventis/map-engine/src/utilities/style-helpers";
import { IconStyle } from "@iventis/domain-model/model/iconStyle";
import { Feature, Geometry } from "geojson";
import { Model } from "@iventis/domain-model/model/model";
import { styled, useWindowSize } from "@iventis/styles";
import { ModelAsset } from "@iventis/map-engine/src/data-store/model-data-store";
import {
    ImportIventisMap,
    LineModelMapImportLayer,
    ModelMapImportLayer,
    MapObjectProperties,
    AreaMapImportLayer,
    IconMapImportLayer,
    LineMapImportLayer,
    PointMapImportLayer,
} from "../types/export-types";
import { createModelLayer, getMapboxLayer } from "../helpers/layer-helpers";
import { Mapbox } from "../mapbox/mapbox";
import { layerMapObjectsToFeature } from "../helpers/geojson-helpers";
import { MapboxSupportedLayers } from "../types/mapbox-types";
import { getMapParams } from "../helpers/url-helpers";
import { getBackgroundJson, getIconAssetUrl, getMapJson, getModelAssetUrl, getModelConfigJson } from "../helpers/asset-helpers";
import { PublicMapSearchComponent } from "./search";
import { KeyComponent } from "./key-image";
import { ToggleKeyComponent } from "./toggle-key";

export const PublicMapComponent: React.FunctionComponent = () => {
    const [mapObjectFeaturesToSearch, setMapObjectFeaturesToSearch] = useState<Feature[]>([]);
    const mapboxInstanceRef = useRef<Mapbox | null>(null);
    const keyImageRef = useRef<string | null>(null);

    const { screenWidth } = useWindowSize();

    // If the device is not mobile, show key by default
    const isMobileDevice = screenWidth < 768;
    const [showKey, setShowKey] = useState(!isMobileDevice);

    const handleFeatureSelected = (feature: Feature) => {
        if (mapboxInstanceRef.current != null) {
            mapboxInstanceRef.current.goToFeature(feature);
        }
    };

    const handleMapContainerRendered = async (element: HTMLDivElement) => {
        // If mapbox has already been created, do nothing
        if (mapboxInstanceRef.current != null) {
            return;
        }

        // Get variables from the URL
        const { bounds, exportMapId, backgroundId, modelsConfigId, layerIdsToSearch, keyImage, level } = getMapParams();
        keyImageRef.current = keyImage;

        const modelConfigResponse = await fetch(getModelConfigJson(modelsConfigId));
        const modelConfig = (await modelConfigResponse.json()) as Model[];

        const getModelIds = async (modelIds: string[]): Promise<Model[]> => modelConfig.filter((model) => modelIds.includes(model.id));

        const getModelAssets = async (assetIds: string[]): Promise<ModelAsset[]> => {
            const models = modelConfig.filter((model) => assetIds.includes(model.lods[0].files[0].assetId));
            return models.map((m) => ({ id: m.lods[0].files[0].assetId, url: getModelAssetUrl(m.lods[0].files[0].assetId) }));
        };

        const mapboxInstance = new Mapbox({ getModelIds, getModelAssets });
        mapboxInstanceRef.current = mapboxInstance;

        // Fetch the map data
        const exportMapResponse = await fetch(getMapJson(exportMapId));
        const exportMap = (await exportMapResponse.json()) as ImportIventisMap;

        // Fetch background json
        const backgroundUrl = await fetch(getBackgroundJson(backgroundId));
        const backgroundJson = await backgroundUrl.json();

        // Create mapbox map
        const mapboxMap = await mapboxInstance.createMap(element, bounds, backgroundJson);

        mapboxInstance.addInternalImages();

        const alreadyLoadedIcons: string[] = [];

        const areaLayers: AreaMapImportLayer[] = [];
        const lineLayers: LineMapImportLayer[] = [];
        const iconLayers: IconMapImportLayer[] = [];
        const pointLayers: PointMapImportLayer[] = [];
        const modelLayers: ModelMapImportLayer[] = [];
        const lineModelLayers: LineModelMapImportLayer[] = [];

        // Break layers up into their respective types so we can order them correctly
        exportMap.layers.forEach((exportedLayer) => {
            switch (exportedLayer.styleType) {
                case StyleType.Area:
                    areaLayers.push(exportedLayer);
                    break;
                case StyleType.Line:
                    lineLayers.push(exportedLayer);
                    break;
                case StyleType.Icon:
                    iconLayers.push(exportedLayer);
                    break;
                case StyleType.Point:
                    pointLayers.push(exportedLayer);
                    break;
                case StyleType.Model:
                    modelLayers.push(exportedLayer);
                    break;
                case StyleType.LineModel:
                    lineModelLayers.push(exportedLayer);
                    break;
                case StyleType.AreaModel:
                    // Area models are not supported in the public map
                    break;
                default:
                    throw new Error(`Unknown style type`);
            }
        });

        const featuresToSearch: Feature[] = [];

        // Load map layers apart from models
        const loadMapLayers = (exportedLayer: AreaMapImportLayer | LineMapImportLayer | IconMapImportLayer | PointMapImportLayer) => {
            const mapObjectNameDataField = exportedLayer.dataFields.find((df) => df.systemDataFieldName === "MapObjectName");

            if (mapObjectNameDataField == null) {
                throw new Error("No data field found for map object name");
            }

            const features = layerMapObjectsToFeature(exportedLayer, mapObjectNameDataField.id, level);

            if (layerIdsToSearch.includes(exportedLayer.id)) {
                featuresToSearch.push(...features);
            }

            // If it is an icon load the icon image
            if (exportedLayer.styleType === StyleType.Icon) {
                loadIcons(exportedLayer.style, alreadyLoadedIcons, mapboxInstance);
            }

            const layers = getMapboxLayer(exportedLayer, mapboxMap);
            // Add the source of geojson
            mapboxInstance.addSource(exportedLayer.id, features);
            layers.forEach((layer) => {
                const mapboxLayer = { ...layer.layer } as MapboxSupportedLayers;
                // Remove the filter not needed
                delete mapboxLayer.filter;
                // If an area needs a centroid source, create it
                if (typeof mapboxLayer.source === "string" && mapboxLayer.source.includes("centroid")) {
                    createCentroidSource(features, mapboxInstance, exportedLayer.id);
                }
                mapboxInstance.addLayer(mapboxLayer);
            });
        };

        // Load the model layers
        const loadModelLayers = (exportedLayer: ModelMapImportLayer | LineModelMapImportLayer) => {
            const mapObjectNameDataField = exportedLayer.dataFields.find((df) => df.systemDataFieldName === "MapObjectName");

            if (mapObjectNameDataField == null) {
                throw new Error("No data field found for map object name");
            }

            const geojson = layerMapObjectsToFeature(exportedLayer, mapObjectNameDataField.id, level);
            const iventisLayer = createModelLayer(exportedLayer);
            mapboxInstance.addModelLayer(iventisLayer, geojson);
        };

        // Load each layer type one by one
        areaLayers.forEach(loadMapLayers);
        lineLayers.forEach(loadMapLayers);

        // Load model layers
        modelLayers.forEach(loadModelLayers);
        lineModelLayers.forEach(loadModelLayers);

        // Load icons and points into the map last so they are on top
        iconLayers.forEach(loadMapLayers);
        pointLayers.forEach(loadMapLayers);

        // Add the map objects to the search component
        setMapObjectFeaturesToSearch(featuresToSearch);
    };

    return (
        <Container>
            {/** If the key image is not loaded, do not render the key or the button to toggle it */}
            {keyImageRef.current != null && (
                <>
                    <KeyComponent showKey={showKey} keyImage={keyImageRef.current} />
                    <ToggleKeyComponent showKey={showKey} setShowKey={setShowKey} />
                </>
            )}
            <MapContainer>
                {mapObjectFeaturesToSearch.length > 0 && <PublicMapSearchComponent features={mapObjectFeaturesToSearch} featureClicked={handleFeatureSelected} />}
                <MapElement ref={handleMapContainerRendered} />
            </MapContainer>
        </Container>
    );
};

/** Loads an icon into mapbox */
function loadIcons(iconStyle: IconStyle, alreadyLoadedIcons: string[], mapboxInstance: Mapbox) {
    const iconIds = getStaticAndMappedValues(iconStyle.iconImage);
    iconIds.forEach((iconId) => {
        if (!alreadyLoadedIcons.includes(iconId)) {
            const iconUrl = getIconAssetUrl(iconId);
            const customColour = iconStyle.customColour.staticValue.staticValue;
            mapboxInstance.loadImage(iconUrl, iconId, customColour);
        }
        alreadyLoadedIcons.push(iconId);
    });
}

/** Creates centroid source for areas with text */
function createCentroidSource(geojson: Feature<Geometry, MapObjectProperties>[], mapboxInstance: Mapbox, layerId: string) {
    const features: Feature[] = [];
    geojson.forEach((feature) => {
        // Check for type safety
        if (feature.geometry.type === "Polygon") {
            const centerPoint = center(feature.geometry);
            features.push({ ...centerPoint, properties: feature.properties });
        }
    });
    mapboxInstance.addSource(`${layerId}_centroid`, features);
}

const Container = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
`;

const MapContainer = styled.div`
    position: relative;
    flex-shrink: 1;
    flex-grow: 1;
`;

const MapElement = styled.div`
    width: 100%;
    height: 100%;
`;
